// src/services/dashboardApi.ts
import api from './api';
import { Device, SystemStatusResponse, SystemStatusMetrics, DeviceHealthStatus, PinageStatus } from '../types/devices';
import { Asset } from '../types/assets';
import { Playlist } from '../types/playlists';
import { parseUTCDate } from '../utils/dateUtils';

export interface DashboardStats {
  totalDevices: number;
  activeDevices: number;
  onlineDevices: number;
  totalPlaylists: number;
  activePlaylists: number;
  totalAssets: number;
  totalStorage: number;
  deviceStatuses: DeviceStatusSummary[];
}

export interface DeviceStatusSummary {
  deviceId: number;
  deviceName: string;
  location: string;

  // Two independent verdicts, both computed by the API on every read from how
  // recently it actually heard from the device.
  //
  // No join against /devices any more. That join existed because the old single
  // `status` was a stored column that froze when a device went quiet, so
  // connectivity had to be fetched separately to correct it. Both of these are
  // derived at read time, so they are already current and there is nothing left
  // to correct.
  healthStatus: DeviceHealthStatus;
  healthReasons?: string[];
  pinageStatus: PinageStatus;
  pinageReasons?: string[];
  cameraDetected?: boolean | null;

  boardModel?: string;
  pinageVersion?: string;
  updatePending?: boolean;

  currentPlaylist?: string;
  lastSync?: Date;
  metrics?: SystemStatusMetrics;
}

export const dashboardApi = {
  getDashboardStats: async (): Promise<DashboardStats> => {
    try {
      // Fetch all required data in parallel
      const [devices, playlists, assets, systemStatuses] = await Promise.all([
        api.get<Device[]>('/devices'),
        api.get<Playlist[]>('/playlists'),
        api.get<Asset[]>('/assets'),
        api.get<SystemStatusResponse[]>('/systemstatus/all')  // Specify the type here
      ]);

      // Calculate statistics
      const totalDevices = devices.data.length;
      const activeDevices = devices.data.filter(d => d.active).length;
      const onlineDevices = devices.data.filter(d => d.onlineStatus === 'Online').length;
      const totalPlaylists = playlists.data.length;
      const activePlaylists = playlists.data.filter(p => p.active).length;
      const totalAssets = assets.data.length;
      const totalStorage = assets.data.reduce((sum, asset) => sum + asset.fileSize, 0);

      // Map system statuses to devices
      const deviceStatuses: DeviceStatusSummary[] = systemStatuses.data.map((status: SystemStatusResponse) => ({
        deviceId: status.deviceId,
        deviceName: status.deviceName,
        location: status.location,

        healthStatus: status.healthStatus,
        healthReasons: status.healthReasons,
        pinageStatus: status.pinageStatus,
        pinageReasons: status.pinageReasons,
        cameraDetected: status.cameraDetected,

        boardModel: status.boardModel,
        pinageVersion: status.pinageVersion,
        updatePending: status.updatePending,

        currentPlaylist: status.currentPlaylist,
        lastSync: status.lastSync ? parseUTCDate(status.lastSync) : undefined,
        metrics: status.metrics ? {
          cpuUsage: status.metrics.cpuUsage,
          memoryUsage: status.metrics.memoryUsage,
          diskUsage: status.metrics.diskUsage,
          temperature: status.metrics.temperature,
          uptimeSeconds: status.metrics.uptimeSeconds,
          signalDbm: status.metrics.signalDbm
        } : undefined
      }));

      return {
        totalDevices,
        activeDevices,
        onlineDevices,  
        totalPlaylists,
        activePlaylists,
        totalAssets,
        totalStorage,
        deviceStatuses
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }
};